/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Layout,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Space,
  Collapse,
  Button,
  Form,
  Typography,
  message,
  InputNumber,
  Tooltip,
  Card
} from 'antd';
import {
  CaretDownOutlined,
  CalendarOutlined,
  ClockCircleFilled,
  SearchOutlined,
  InfoCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Alert from 'components/Alert';
import leadsAPI from 'services/leads';
import {
  state,
  businessEntityType,
  productList,
  leadStatus,
  accountSegment,
  industry,
  competitiorCurrently,
  leadRegion
} from 'constants';
import utilsAPI from 'services/other';
import currencyFormat from 'utils/currencyFormat';
import trackingService from 'services/trackingService';
import EVENT_NAME from 'utils/const';

const { Content } = Layout;
const { Panel } = Collapse;
const { Meta } = Card;

const CustomCollapse = styled(Collapse)`
  .ant-collapse-item > .ant-collapse-header {
    background-color: var(--color-fill-alter) !important;
    border-radius: 5px !important;
  }

  .ant-collapse-content-box {
    padding: 16px 0px !important;
  }
`;

const FormWrapper = styled(Form)`
  .ant-form-item {
    margin-bottom: 15px;
  }

  .ant-form-item-label {
    padding: 0px;
  }

  .ant-form-item
    .ant-form-item-label
    > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    content: '';
  }

  .required-mark:before {
    color: red;
    content: '*';
    font-size: 21px;
  }
`;

const MetaWrapper = styled(Meta)`
  && {
    width: 100%;
    margin-top: 2%;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
  }
 var(--color-fill-alter
  .ant-card-meta-description {
    font-size: 0.8rem;
    padding-right: 2%;
  }
`;

function CreateLeadPage() {
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [data, setData] = useState({});
  const navigate = useNavigate();

  const [leadForm] = Form.useForm();

  const initailValues = {
    business_entity_type: 'NA',
    product_required: 'NA',
    priority: 'LOW',
    status: 'NEW',
    sources: 'PARTNER',
    account_segment: 'NA',
    industry: 'NA',
    current_competitior: 'NA'
  };

  const handleFormSubmit = async (v) => {
    setLoading(true);

    const payload = {
      basic_details: {
        name_of_the_person: v.name_of_the_person ? v.name_of_the_person : null,
        name_of_business: v.name_of_business ? v.name_of_business : null,
        email_id: v.email_id ? v.email_id : null,
        phone_number: v.phone_number ? v.phone_number : null,
        business_entity_type: v.business_entity_type ? v.business_entity_type : 'NA',
        product_required: v.product_required ? v.product_required : 'NA',
        business_requirement: v.business_requirement ? v.business_requirement : null
      },
      lead_details: {
        priority: v.priority ? v.priority : null,
        status: v.status ? v.status : null,
        sources: v.sources ? v.sources : null,
        tags: v.tags ? v.tags.join(';') : null,
        remarks: v.remarks ? v.remarks : null
      },
      demo_details: {
        demo_required: v.demo_required ? v.demo_required : null,
        demo_date_time:
          v.date && v.time
            ? `${dayjs(v.date).format('YYYY-MM-DD')} ${dayjs(v.time).format('HH:mm:ss')}`
            : null
      },
      additional_details: {
        address_details: {
          address: v.address ? v.address : null,
          city: v.city ? v.city : null,
          state: v.state ? v.state : null,
          pincode: v.pincode ? v.pincode : null,
          region: v.region ? v.region : 'NA'
        },
        commercial_details: {
          current_competitior: v.current_competitior ? v.current_competitior : 'NA',
          account_segment: v.account_segment ? v.account_segment : 'NA',
          potential_monthly_gtv: v.potential_monthly_gtv ? v.potential_monthly_gtv : null,
          industry: v.industry ? v.industry : 'NA'
        }
      }
    };

    const response = await leadsAPI.create(payload);

    setData(response?.data ?? []);
    setLoading(false);

    if (response.success) {
      message.success({
        content: 'Lead created successfully'
      });

      leadForm.resetFields();

      navigate('/leads');
    } else {
      Alert('Oops!!!', 'Something went wrong. Try again.', 'error');
      setHasData(false);
    }
  };
  const handleTrackingService = (event, eventName) => {
    const trackingProperty = {};
    if (eventName === EVENT_NAME.DROPDOWN || eventName === EVENT_NAME.DATE_PICKER) {
      trackingProperty.selected_value = event?.value;
      trackingProperty.name = event?.name;
    } else {
      trackingProperty.name = event?.target?.name;
    }
    trackingService.trackEvent(eventName, trackingProperty);
  };

  const fetchStateCityByPincode = async (payload) => {
    const response = await utilsAPI.getPincode({ id: payload });

    message.info({
      content: 'Fetching State and City from pincode'
    });

    leadForm.setFields([
      {
        name: 'state',
        disabled: 'true'
      }
    ]);

    if (response.status === 200) {
      leadForm.setFields([
        {
          name: 'state',
          value: response.data[0].PostOffice[0].State
        },
        {
          name: 'city',
          value: response.data[0].PostOffice[0].Region
        }
      ]);
    } else {
      message.error({
        content: 'Fetching State and City from pincode failed'
      });
    }
  };

  const handleFieldsValueChange = (e) => {
    if ('pincode' in e && e.pincode.length === 6) {
      fetchStateCityByPincode(e.pincode);
    }
  };

  return (
    <Layout style={{ background: '#f5f5f500' }}>
      <Content style={{ marginLeft: '30px' }}>
        <Link to="/leads" style={{ marginLeft: '5px' }}>
          <Button
            onClick={() => {
              trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, {
                name: 'Lead Back Click'
              });
            }}
          >
            <ArrowLeftOutlined />
            Back
          </Button>
        </Link>
        <FormWrapper
          form={leadForm}
          name="create-lead"
          layout="vertical"
          initialValues={initailValues}
          onFinish={handleFormSubmit}
          onValuesChange={handleFieldsValueChange}
        >
          <Row>
            <Col
              span={21}
              style={{
                marginTop: '25px',
                background: '#FFFFFF',
                boxShadow: '0px 3px 6px #0000001C',
                borderRadius: '10px',
                padding: '20px 40px'
              }}
            >
              <Row>
                <Col span={24}>
                  <Typography.Title
                    level={4}
                    style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '500',
                      width: '100%'
                    }}
                  >
                    Basic Details
                  </Typography.Title>
                  <Row
                    gutter={[20, 0]}
                    style={{
                      marginTop: '15px'
                    }}
                  >
                    <Col span={8}>
                      <Form.Item
                        name="name_of_business"
                        label={
                          <>
                            <span>Name of the business</span>
                            <span className="required-mark" />
                            <Tooltip title="Only alpha-numeric, spaces and dashes allowed">
                              <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                            </Tooltip>
                          </>
                        }
                        rules={[
                          {
                            required: true,
                            message: 'Please input your Name of the business'
                          },
                          {
                            message: 'Please enter a valid Name of the business.',
                            pattern: '^[a-zA-Z0-9_ -]+[w-]*$'
                          },
                          {
                            validator(_, value) {
                              if (
                                value !== undefined &&
                                value.length !== 0 &&
                                value.trim().length === 0
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject('Please enter valid character');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          name="name_of_business"
                          onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                          maxLength={100}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="name_of_the_person"
                        label={
                          <>
                            <span>Name of the Person</span>
                            <span className="required-mark" />
                            <Tooltip title="Only alphabet allowed">
                              <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                            </Tooltip>
                          </>
                        }
                        rules={[
                          {
                            required: true,
                            message: 'Please input your Name of the person'
                          },
                          {
                            message: 'Please enter a valid Name of the person.',
                            pattern: '^[a-zA-Z_ -]+[w-]*$'
                          },
                          {
                            validator(_, value) {
                              if (
                                value !== undefined &&
                                value.length !== 0 &&
                                value.trim().length === 0
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject('Please enter valid character');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          name="name_of_the_person"
                          onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                          maxLength={100}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="phone_number"
                        label={
                          <>
                            <span>Phone Number</span>
                            <span className="required-mark" />
                          </>
                        }
                        rules={[
                          {
                            required: true,
                            message: 'Please input your Phone number'
                          },
                          {
                            message: 'Please enter valid Phone number',
                            pattern: '^[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4}$'
                            // pattern: '^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$'
                          }
                        ]}
                      >
                        <Input
                          name="phone_number"
                          onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="email_id"
                        label="Email ID"
                        rules={[
                          {
                            validator(_, value) {
                              if (
                                !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(value) &&
                                value !== undefined &&
                                value !== ''
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject('Please enter valid Email');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          name="email_id"
                          onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="business_entity_type" label="Business Entity Type">
                        <Select
                          name="business_entity_type"
                          onChange={(value) => {
                            handleTrackingService(
                              { name: 'business_entity_type', value },
                              EVENT_NAME.DROPDOWN
                            );
                          }}
                          suffixIcon={
                            <CaretDownOutlined style={{ fontSize: '15px', color: '#2A2A2A' }} />
                          }
                          style={{
                            width: '100%'
                          }}
                          options={businessEntityType}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="product_required" label="Product Required">
                        <Select
                          onChange={(value) => {
                            handleTrackingService(
                              { name: 'product_required', value },
                              EVENT_NAME.DROPDOWN
                            );
                          }}
                          suffixIcon={
                            <SearchOutlined
                              style={{ fontSize: '15px', color: 'var(--color-primary)' }}
                            />
                          }
                          style={{
                            width: '100%'
                          }}
                          options={productList}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="business_requirement"
                        label={
                          <>
                            <span>Business Requirement</span>
                            <Tooltip title="Only alpha-numeric, spaces and dashes allowed">
                              <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                            </Tooltip>
                          </>
                        }
                        rules={[
                          {
                            message: 'Please enter a valid Business Requirement.',
                            pattern: '^[a-zA-Z0-9_ -]+[w-]*$'
                          },
                          {
                            validator(_, value) {
                              if (
                                value !== undefined &&
                                value.length !== 0 &&
                                value.trim().length === 0
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject('Please enter valid character');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          name="business_requirement"
                          onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                          maxLength={100}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row style={{ marginTop: '30px' }}>
                <Col span={24}>
                  <Typography.Title
                    level={4}
                    style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '500',
                      width: '100%'
                    }}
                  >
                    Lead Information
                  </Typography.Title>
                  <Row
                    gutter={[20, 0]}
                    style={{
                      marginTop: '15px'
                    }}
                  >
                    <Col span={8}>
                      <Form.Item name="status" label="Lead Status">
                        <Select
                          suffixIcon={
                            <CaretDownOutlined style={{ fontSize: '15px', color: '#2A2A2A' }} />
                          }
                          style={{
                            width: '100%'
                          }}
                          disabled
                          options={leadStatus}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="sources" label="Lead Source">
                        <Select
                          suffixIcon={
                            <CaretDownOutlined style={{ fontSize: '15px', color: '#2A2A2A' }} />
                          }
                          style={{
                            width: '100%'
                          }}
                          disabled
                          options={[
                            {
                              value: 'PARTNER',
                              label: 'Partner'
                            }
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="priority" label="Lead Priority">
                        <Select
                          onChange={(value) => {
                            handleTrackingService(
                              { name: 'Lead_Priority', value },
                              EVENT_NAME.DROPDOWN
                            );
                          }}
                          suffixIcon={
                            <CaretDownOutlined style={{ fontSize: '15px', color: '#2A2A2A' }} />
                          }
                          style={{
                            width: '100%'
                          }}
                          options={[
                            {
                              value: 'LOW',
                              label: 'Low'
                            },
                            {
                              value: 'MEDIUM',
                              label: 'Medium'
                            },
                            {
                              value: 'HIGH',
                              label: 'High'
                            }
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="tags" label="Tags">
                        <Select
                          onChange={(value) => {
                            handleTrackingService({ name: 'tags', value }, EVENT_NAME.DROPDOWN);
                          }}
                          suffixIcon={
                            <CaretDownOutlined style={{ fontSize: '15px', color: '#2A2A2A' }} />
                          }
                          showArrow
                          mode="tags"
                          style={{
                            width: '100%'
                          }}
                          tokenSeparators={[';']}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        name="remarks"
                        label={
                          <>
                            <span>Remarks</span>
                            <Tooltip title="Only alpha-numeric, spaces and dashes allowed">
                              <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                            </Tooltip>
                          </>
                        }
                        rules={[
                          {
                            message: 'Please enter a valid Remarks.',
                            pattern: '^[a-zA-Z0-9_ -]+[w-]*$'
                          },
                          {
                            validator(_, value) {
                              if (
                                value !== undefined &&
                                value.length !== 0 &&
                                value.trim().length === 0
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject('Please enter valid character');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          name="remarks"
                          onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                          maxLength={100}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row style={{ marginTop: '20px' }}>
                <Col span={24}>
                  <Typography.Title
                    level={4}
                    style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '500',
                      width: '100%'
                    }}
                  >
                    Demo Details
                  </Typography.Title>
                  <Row
                    gutter={[20, 0]}
                    style={{
                      marginTop: '15px'
                    }}
                  >
                    <Col span={8}>
                      <Form.Item name="demo_required" label="Demo Required">
                        <Select
                          onChange={(value) => {
                            handleTrackingService(
                              { name: 'demo_required', value },
                              EVENT_NAME.DROPDOWN
                            );
                          }}
                          suffixIcon={
                            <CaretDownOutlined style={{ fontSize: '15px', color: '#2A2A2A' }} />
                          }
                          style={{
                            width: '100%'
                          }}
                          options={[
                            {
                              value: 'Yes',
                              label: 'Yes'
                            },
                            {
                              value: 'No',
                              label: 'No'
                            }
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="date_item" label="Demo Date & Time">
                        <Space style={{ width: '100%' }}>
                          <Form.Item name="date">
                            <DatePicker
                              name="date"
                              onChange={(value) => {
                                handleTrackingService(
                                  { name: 'date', value: value?.format('YYYY-MM-DD') },
                                  EVENT_NAME.DATE_PICKER
                                );
                              }}
                              suffixIcon={
                                <CalendarOutlined
                                  style={{ fontSize: '15px', color: 'var(--color-primary)' }}
                                />
                              }
                            />
                          </Form.Item>
                          <Form.Item name="time">
                            <TimePicker
                              onChange={(value) => {
                                handleTrackingService(
                                  { name: 'time', value: value?.format('hh:mm:ss A') },
                                  EVENT_NAME.DATE_PICKER
                                );
                              }}
                              suffixIcon={
                                <ClockCircleFilled
                                  style={{ fontSize: '15px', color: 'var(--color-primary)' }}
                                />
                              }
                            />
                          </Form.Item>
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row style={{ marginTop: '10px' }}>
                <CustomCollapse
                  bordered={false}
                  expandIconPosition="end"
                  // eslint-disable-next-line react/no-unstable-nested-components
                  expandIcon={({ isActive }) => (
                    <CaretDownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: '20px' }} />
                  )}
                  style={{ width: '100%', backgroundColor: '#FFFFFF' }}
                >
                  <Panel
                    style={{ backgroundColor: '#FFFFFF' }}
                    // eslint-disable-next-line react/no-unstable-nested-components
                    header={
                      <Typography.Title
                        level={4}
                        style={{
                          margin: 0,
                          fontSize: '20px',
                          fontWeight: '500',
                          width: '100%'
                        }}
                      >
                        Additional Information
                      </Typography.Title>
                    }
                    key="1"
                  >
                    <Row style={{ marginTop: '10px' }}>
                      <Col span={24}>
                        <Typography.Title
                          level={4}
                          style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: '500',
                            width: '100%'
                          }}
                        >
                          Address Details
                        </Typography.Title>
                        <Row
                          gutter={[20, 0]}
                          style={{
                            marginTop: '15px'
                          }}
                        >
                          <Col span={16}>
                            <Form.Item
                              name="address"
                              label={
                                <>
                                  <span>Address</span>
                                  <Tooltip title="Only alpha-numeric, spaces and dashes allowed">
                                    <InfoCircleOutlined
                                      style={{ color: '#A0A0A0', marginLeft: '5px' }}
                                    />
                                  </Tooltip>
                                </>
                              }
                              rules={[
                                {
                                  message: 'Please enter a valid Address.',
                                  pattern: '^[a-zA-Z0-9_ -]+[w-]*$'
                                },
                                {
                                  validator(_, value) {
                                    if (
                                      value !== undefined &&
                                      value.length !== 0 &&
                                      value.trim().length === 0
                                    ) {
                                      // eslint-disable-next-line prefer-promise-reject-errors
                                      return Promise.reject('Please enter valid character');
                                    }
                                    return Promise.resolve();
                                  }
                                }
                              ]}
                            >
                              <Input
                                name="address"
                                onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                                maxLength={100}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="pincode"
                              label="Pincode"
                              rules={[
                                {
                                  message: 'Please enter a valid Pincode.',
                                  pattern: '^[1-9][0-9]{5}$'
                                }
                              ]}
                            >
                              <Input
                                name="pincode"
                                onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="city"
                              label={
                                <>
                                  <span>City</span>
                                  <Tooltip title="Only alphabet allowed">
                                    <InfoCircleOutlined
                                      style={{ color: '#A0A0A0', marginLeft: '5px' }}
                                    />
                                  </Tooltip>
                                </>
                              }
                              rules={[
                                {
                                  message: 'Please enter a valid City.',
                                  pattern: '^[a-zA-Z0-9_ -]+[w-]*$'
                                },
                                {
                                  validator(_, value) {
                                    if (
                                      value !== undefined &&
                                      value.length !== 0 &&
                                      value.trim().length === 0
                                    ) {
                                      // eslint-disable-next-line prefer-promise-reject-errors
                                      return Promise.reject('Please enter valid character');
                                    }
                                    return Promise.resolve();
                                  }
                                }
                              ]}
                            >
                              <Input
                                name="city"
                                onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                                maxLength={100}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item name="state" label="State">
                              <Select
                                onChange={(value) => {
                                  handleTrackingService(
                                    { name: 'state', value },
                                    EVENT_NAME.DROPDOWN
                                  );
                                }}
                                suffixIcon={
                                  <CaretDownOutlined
                                    style={{ fontSize: '15px', color: '#2A2A2A' }}
                                  />
                                }
                                style={{
                                  width: '100%'
                                }}
                                options={state}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item name="region" label="Region">
                              <Select
                                onChange={(value) => {
                                  handleTrackingService(
                                    { name: 'region', value },
                                    EVENT_NAME.DROPDOWN
                                  );
                                }}
                                suffixIcon={
                                  <CaretDownOutlined
                                    style={{ fontSize: '15px', color: '#2A2A2A' }}
                                  />
                                }
                                style={{
                                  width: '100%'
                                }}
                                options={leadRegion}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <Row style={{ marginTop: '10px' }}>
                      <Col span={24}>
                        <Typography.Title
                          level={4}
                          style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: '500',
                            width: '100%'
                          }}
                        >
                          Commercials Details
                        </Typography.Title>
                        <Row
                          gutter={[20, 0]}
                          style={{
                            marginTop: '15px'
                          }}
                        >
                          <Col span={16}>
                            <Form.Item name="account_segment" label="Account Segment">
                              <Select
                                onChange={(value) => {
                                  handleTrackingService(
                                    { name: 'account_segment', value },
                                    EVENT_NAME.DROPDOWN
                                  );
                                }}
                                suffixIcon={
                                  <CaretDownOutlined
                                    style={{ fontSize: '15px', color: '#2A2A2A' }}
                                  />
                                }
                                style={{
                                  width: '100%'
                                }}
                                options={accountSegment}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name="potential_monthly_gtv"
                              label="Potential Monthly GTV"
                              // rules={[
                              //   {
                              //     message: 'Please enter a Potential Monthly GTV.',
                              //     pattern: '^[0-9]*$'
                              //   }
                              // ]}
                            >
                              {/* <Input
                                formatter={(v) => {
                                  if (v !== '') return currencyFormat(v, false, false);
                                  return v;
                                }}
                                prefix="₹"
                              /> */}
                              <InputNumber
                                name="potential_monthly_gtv"
                                onClick={(e) => handleTrackingService(e, EVENT_NAME.TEXT_INPUT)}
                                prefix="₹"
                                controls={false}
                                style={{ width: '100%' }}
                                formatter={(v) => {
                                  if (v !== '') return currencyFormat(v, false, false);
                                  return v;
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item name="industry" label="Industry">
                              <Select
                                onChange={(value) => {
                                  handleTrackingService(
                                    { name: 'industry', value },
                                    EVENT_NAME.DROPDOWN
                                  );
                                }}
                                suffixIcon={
                                  <CaretDownOutlined
                                    style={{ fontSize: '15px', color: '#2A2A2A' }}
                                  />
                                }
                                style={{
                                  width: '100%'
                                }}
                                options={industry}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item name="current_competitior" label="Competitor Currently">
                              <Select
                                onChange={(value) => {
                                  handleTrackingService(
                                    { name: 'current_competitior', value },
                                    EVENT_NAME.DROPDOWN
                                  );
                                }}
                                suffixIcon={
                                  <CaretDownOutlined
                                    style={{ fontSize: '15px', color: '#2A2A2A' }}
                                  />
                                }
                                style={{
                                  width: '100%'
                                }}
                                options={competitiorCurrently}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Panel>
                </CustomCollapse>
              </Row>
              <Row style={{ marginTop: '10px' }}>
                <Col span={24}>
                  <MetaWrapper
                    description="* Lead generated will be subject to review as per the
                    company’s policy and Partner’s Terms of Use."
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col
              span={21}
              style={{
                marginTop: '25px'
              }}
            >
              <Form.Item
                style={{
                  marginTop: '25px',
                  marginLeft: '30px'
                }}
              >
                <Space align="end" style={{ display: 'flex', justifyContent: 'end' }}>
                  <Button type="primary" htmlType="submit" size="large" loading={loading}>
                    {loading ? 'Creating Lead...' : 'Submit'}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </FormWrapper>
      </Content>
    </Layout>
  );
}

export default CreateLeadPage;
